/**
 * utils/dateTime.ts
 *
 * Formatage des dates/heures dans le fuseau de l'ASSOCIATION (paramètre
 * `default_timezone`) et non dans celui du navigateur : un administrateur
 * connecté depuis Paris doit voir l'heure du Maroc.
 *
 * Le fuseau est résolu via `Intl` (nom IANA) et non par un décalage fixe :
 * le Maroc passe à UTC+0 pendant le Ramadan, seul un nom IANA le gère.
 */

/** Construit un formateur avec le fuseau demandé, repli sur le navigateur si le nom est invalide. */
function formatterFor(locale: string, timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat(locale, options);
  }
}

/** Date longue (« الجمعة، 2 أكتوبر 2026 ») dans le fuseau de l'association. */
export function formatDateInTz(date: Date, timeZone: string, locale: string): string {
  return formatterFor(locale, timeZone, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  }).format(date);
}

/** Heure (HH:mm par défaut) dans le fuseau de l'association. */
export function formatTimeInTz(
  date: Date,
  timeZone: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
  return formatterFor(locale, timeZone, options).format(date);
}

/** Date courte JJ/MM/AAAA dans le fuseau de l'association. */
export function formatShortDateInTz(date: Date, timeZone: string, locale: string): string {
  return formatterFor(locale, timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

/** Date du jour au format `YYYY-MM-DD` dans le fuseau de l'association. */
export function todayInTz(timeZone: string): string {
  const parts = formatterFor('en-CA', timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date());
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Fuseau de l'association lu dans le cache local (`assoc_cache_v2`, écrit par `AssociationContext`).
 *
 * Utile pour les helpers qui n'ont pas accès au contexte React (ex. `GLOBAL_UTILS`).
 * Repli : le fuseau du navigateur (jamais pire que l'ancien comportement).
 */
export function getAppTimezone(): string {
  try {
    const tz = JSON.parse(localStorage.getItem('assoc_cache_v2') || '{}')?.timezone;
    if (typeof tz === 'string' && tz.trim()) {
      new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() }); // valide « +00:00 » comme « Europe/Paris »
      return tz.trim();
    }
  } catch {
    /* cache absent ou illisible */
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/** Date `YYYY-MM-DD` (jour calendaire) d'un instant, dans le fuseau de l'association. */
export function dateStrInTz(date: Date, timeZone: string): string {
  const parts = formatterFor('en-CA', timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
